
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, Music, Send, Database, Copy, UploadCloud, CheckCircle2, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';

// Sample catalog data
const sampleCatalog = [
  {
    id: 1,
    title: "Summer Nights",
    writers: "Jane Smith, John Doe",
    publisher: "Indie Publishing Co.",
    registrations: {
      ascap: "Registered",
      bmi: "Not Registered",
      sesac: "Not Applicable",
      hfa: "Pending",
      soundExchange: "Registered"
    },
    lastUpdated: "2024-01-15"
  },
  {
    id: 2,
    title: "Midnight Blues",
    writers: "John Doe",
    publisher: "Indie Publishing Co.",
    registrations: {
      ascap: "Not Applicable",
      bmi: "Registered",
      sesac: "Not Applicable",
      hfa: "Registered",
      soundExchange: "Registered"
    },
    lastUpdated: "2024-02-20"
  },
  {
    id: 3,
    title: "Echoes of Yesterday",
    writers: "Jane Smith, Alex Johnson",
    publisher: "Indie Publishing Co.",
    registrations: {
      ascap: "Registered",
      bmi: "Not Applicable",
      sesac: "Not Applicable",
      hfa: "Pending",
      soundExchange: "Not Registered"
    },
    lastUpdated: "2024-03-05"
  }
];

// Rights organizations data
const rightsOrganizations = [
  {
    id: "ascap",
    name: "ASCAP",
    type: "Performance Rights Organization",
    description: "American Society of Composers, Authors, and Publishers",
    icon: <Music className="w-5 h-5 text-blue-500" />
  },
  {
    id: "bmi",
    name: "BMI",
    type: "Performance Rights Organization",
    description: "Broadcast Music, Inc.",
    icon: <Music className="w-5 h-5 text-blue-500" />
  },
  {
    id: "sesac",
    name: "SESAC",
    type: "Performance Rights Organization",
    description: "Society of European Stage Authors and Composers",
    icon: <Music className="w-5 h-5 text-blue-500" />
  },
  {
    id: "hfa",
    name: "HFA",
    type: "Mechanical Rights Agency",
    description: "Harry Fox Agency",
    icon: <FileText className="w-5 h-5 text-green-500" />
  },
  {
    id: "soundExchange",
    name: "SoundExchange",
    type: "Digital Performance Rights",
    description: "Digital performance rights for sound recordings",
    icon: <Database className="w-5 h-5 text-purple-500" />
  }
];

// Submission types
const submissionTypes = [
  { id: "newWork", label: "New Work Registration" },
  { id: "updateWork", label: "Update Existing Work" },
  { id: "bulkRegistration", label: "Bulk Registration" }
];

const MusicRightsAdministration: React.FC = () => {
  const [activeTab, setActiveTab] = useState("catalog");
  const [isSubmissionDialogOpen, setIsSubmissionDialogOpen] = useState(false);
  const [selectedOrganization, setSelectedOrganization] = useState<string | undefined>();
  const [submissionType, setSubmissionType] = useState<string | undefined>();
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  
  // Function to determine badge color based on registration status
  const getBadgeVariant = (status: string) => {
    switch (status) {
      case "Registered":
        return "bg-green-500/20 text-green-500 border-green-500/30";
      case "Pending":
        return "bg-yellow-500/20 text-yellow-400 border-yellow-400/30";
      case "Not Registered":
        return "bg-slate-500/20 text-slate-400 border-slate-400/30";
      default:
        return "bg-slate-500/20 text-slate-400 border-slate-400/30";
    }
  };

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle className="text-xl flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Music Rights Administration
        </CardTitle>
        <CardDescription>
          Manage your catalog and streamline registrations with PROs and rights agencies
        </CardDescription>
      </CardHeader>
      
      <CardContent>
        <Tabs 
          defaultValue="catalog" 
          value={activeTab} 
          onValueChange={setActiveTab}
          className="space-y-4"
        >
          <TabsList className="grid grid-cols-3 w-full max-w-md">
            <TabsTrigger value="catalog">Catalog</TabsTrigger>
            <TabsTrigger value="submissions">Submissions</TabsTrigger>
            <TabsTrigger value="templates">Templates</TabsTrigger>
          </TabsList>
          
          <TabsContent value="catalog" className="space-y-4">
            <div className="flex justify-between items-center flex-wrap gap-4">
              <div className="flex items-center gap-2">
                <Input
                  className="w-full md:w-64"
                  placeholder="Search your catalog..."
                />
                <Select>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All works</SelectItem>
                    <SelectItem value="registered">Registered</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="notRegistered">Not Registered</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <Button className="flex items-center gap-1">
                <Music className="w-4 h-4" />
                <span>Add Work</span>
              </Button>
            </div>
            
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Writers</TableHead>
                    <TableHead>Publisher</TableHead>
                    <TableHead>Registrations</TableHead>
                    <TableHead>Last Updated</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sampleCatalog.map((work) => (
                    <TableRow key={work.id}>
                      <TableCell className="font-medium">{work.title}</TableCell>
                      <TableCell>{work.writers}</TableCell>
                      <TableCell>{work.publisher}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1.5">
                          {Object.entries(work.registrations).map(([org, status]) => {
                            if (status === "Not Applicable") return null;
                            const orgData = rightsOrganizations.find(o => o.id === org);
                            return (
                              <Badge 
                                key={org} 
                                className={getBadgeVariant(status)}
                                variant="outline"
                              >
                                <span className="text-xs">{orgData?.name}: {status}</span>
                              </Badge>
                            );
                          })}
                        </div>
                      </TableCell>
                      <TableCell>{work.lastUpdated}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm">Edit</Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => {
                              setIsSubmissionDialogOpen(true);
                            }}
                          >
                            Register
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </TabsContent>
          
          <TabsContent value="submissions" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rightsOrganizations.map((org) => (
                <Card key={org.id} className="hover:shadow-md transition-shadow">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      {org.icon}
                      {org.name}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {org.type}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-0 space-y-3">
                    <p className="text-sm">{org.description}</p>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="default" 
                        size="sm" 
                        className="w-full"
                        onClick={() => {
                          setSelectedOrganization(org.id);
                          setIsSubmissionDialogOpen(true);
                        }}
                      >
                        Submit Works
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            <Card className="bg-muted/50 border-dashed">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Recent Submissions</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between items-center p-2 bg-background rounded-md">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-muted-foreground" />
                      <div>
                        <p className="text-sm font-medium">Bulk Registration to BMI</p>
                        <p className="text-xs text-muted-foreground">3 works • Submitted on Jun 15, 2024</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-yellow-500/20 text-yellow-400 border-yellow-400/30">
                      Pending
                    </Badge>
                  </div>
                  
                  <div className="flex justify-between items-center p-2 bg-background rounded-md">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                      <div>
                        <p className="text-sm font-medium">New Work Registration to ASCAP</p>
                        <p className="text-xs text-muted-foreground">"Summer Nights" • Submitted on Jun 10, 2024</p>
                      </div>
                    </div>
                    <Badge variant="outline" className="bg-green-500/20 text-green-500 border-green-500/30">
                      Complete
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="templates" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsTemplateDialogOpen(true)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-4 h-4 text-primary" />
                    Standard Work Registration
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">Basic template for registering a single work with PROs.</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsTemplateDialogOpen(true)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Copy className="w-4 h-4 text-primary" />
                    Bulk Registration CSV
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">Template for bulk upload of multiple works.</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsTemplateDialogOpen(true)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Database className="w-4 h-4 text-primary" />
                    Sound Recording Registration
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">Template for registering sound recordings with SoundExchange.</p>
                </CardContent>
              </Card>
              
              <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => setIsTemplateDialogOpen(true)}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Music className="w-4 h-4 text-primary" />
                    Publishing Agreement
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-muted-foreground">Template for standard publishing agreements.</p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
        
        {/* Submission Dialog */}
        <Dialog open={isSubmissionDialogOpen} onOpenChange={setIsSubmissionDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {selectedOrganization 
                  ? `Submit to ${rightsOrganizations.find(o => o.id === selectedOrganization)?.name}` 
                  : "Register Work"}
              </DialogTitle>
              <DialogDescription>
                Fill out the required information to register your work.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {!selectedOrganization && (
                <div className="space-y-2">
                  <Label htmlFor="organization">Select Organization</Label>
                  <Select 
                    value={selectedOrganization} 
                    onValueChange={setSelectedOrganization}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Choose organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {rightsOrganizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          {org.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="submissionType">Submission Type</Label>
                <Select 
                  value={submissionType} 
                  onValueChange={setSubmissionType}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose submission type" />
                  </SelectTrigger>
                  <SelectContent>
                    {submissionTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {submissionType === "bulkRegistration" ? (
                <div className="space-y-2">
                  <Label>Upload CSV File</Label>
                  <div className="border-2 border-dashed rounded-md p-6 flex flex-col items-center justify-center gap-2">
                    <UploadCloud className="w-8 h-8 text-muted-foreground" />
                    <p className="text-sm text-center text-muted-foreground">
                      Drag and drop your CSV file here, or click to browse
                    </p>
                    <Button variant="outline" size="sm">Browse Files</Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Make sure your CSV follows the required format. 
                    <Button variant="link" className="h-auto p-0 text-xs" onClick={() => {
                      setIsSubmissionDialogOpen(false);
                      setActiveTab("templates");
                    }}>
                      Download template
                    </Button>
                  </p>
                </div>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="workTitle">Work Title</Label>
                    <Input id="workTitle" placeholder="Enter work title" />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="writers">Writers/Composers</Label>
                    <Input id="writers" placeholder="e.g. John Doe, Jane Smith" />
                    <p className="text-xs text-muted-foreground">
                      Separate multiple writers with commas
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="publishers">Publishers</Label>
                    <Input id="publishers" placeholder="e.g. Indie Publishing Co." />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="notes">Additional Information</Label>
                    <Textarea 
                      id="notes" 
                      placeholder="Add any additional details about the work"
                      rows={3}
                    />
                  </div>
                </>
              )}
              
              <div className="pt-2 flex justify-end">
                <Button 
                  className="flex items-center gap-1"
                  onClick={() => setIsSubmissionDialogOpen(false)}
                >
                  <Send className="w-4 h-4" />
                  <span>Submit Registration</span>
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Template Dialog */}
        <Dialog open={isTemplateDialogOpen} onOpenChange={setIsTemplateDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Standard Work Registration Template</DialogTitle>
              <DialogDescription>
                Download or copy this template for your work registrations.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="bg-muted rounded-md p-4 text-sm">
                <pre className="whitespace-pre-wrap">
{`Work Title: 
Writers/Composers: 
Percentage Splits: 
Publisher(s): 
ISWC (if known): 
Alternate Titles: 
Genre: 
Year of Composition: 
Performance Rights Organization: 
IPI/CAE Number (if known): `}
                </pre>
              </div>
              
              <div className="flex justify-between">
                <Button variant="outline" size="sm">
                  <Copy className="w-4 h-4 mr-1" />
                  Copy to Clipboard
                </Button>
                <Button size="sm">
                  Download Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MusicRightsAdministration;
